import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Type from 'components/Type'
import Icon from 'components/Icon'
import Button from 'components/Button'
import Currency from 'components/Currency'
import Select from 'components/Select'
import TextButton from 'components/TextButton'
import TextArea from 'components/TextArea'
import Input from 'components/Input'
import ModalFlow from 'components/ModalFlow'
import CircleFrame from 'components/CommonUI/CircleFrame'
import ExternalAccounts from 'components/CommonUI/ExternalAccounts'
import StandardModal from 'components/CommonUI/StandardModal'
import AllSet from 'components/ModalViews/AllSet'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import {
  GET_MAX_WITHDRAWAL,
  REQUEST_FUND_WITHDRAWAL,
} from 'api/constants'
import { useTypeLoad } from 'utils/hooks'
import { useSavingsTotals } from 'utils/actions'

const REASONS = [
  "I'm no longer with my company",
  "Requesting personal funds for an emergency",
  "Trying to book travel outside of Dónde",
  "Requesting personal funds for on-trip expenses",
  "Other",
]

const RequestWithdrawal = props => {
  const dispatch = useDispatch()
  const user = useSelector(({ global }) => global.user)
  const company = useSelector(({ global }) => global.company)
  const maxWithdrawal = useSelector(({ global }) => global.maxWithdrawal)
  const {
    bank_actual_total,
  } = useSavingsTotals()
  const [activeIndex, setActiveIndex] = useState(0)
  const [account, setAccount] = useState(null)
  const [amount, setAmount] = useState(props.amount)
  const [reason, setReason] = useState(props.reason)
  const [customReason, setCustomReason] = useState('')
  const isDisabled = 
    !account ||
    !+amount ||
    +amount > +bank_actual_total ||
    (reason === REASONS[4] ? !customReason.length : !!reason?.length)


  const handleRequestWithdrawal = () => {
    setActiveIndex(-1)
    dispatch({ 
      type: REQUEST_FUND_WITHDRAWAL,
      data: {
        amount: +amount, 
        reason: reason === REASONS[4] && customReason?.length ? customReason : reason, 
        to_account: account?.name,
        to_account_id: account?.id,
      },
    })
  }

  useTypeLoad(REQUEST_FUND_WITHDRAWAL, data => {
    if (!data.error) setActiveIndex(1)
  })

  useEffect(() => {
    dispatch({ type: GET_MAX_WITHDRAWAL })
  }, [])

  return (
    <ModalFlow
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      sizeX="md"
      loaderLabel="Submitting Request"
    >
      {/* REQUEST */}
      <Stack
        pt={3}
        px={3}
        pb={2}
      >
        <CircleFrame
          mx="auto"
          mb={2}
        >
          <Icon
            icon="CelFundWithdrawal"
          />
        </CircleFrame>
        <Type
          variant="sectionLabel"
          textAlign="center"
          mb={1.5}
        >
          Request to withdraw funds
        </Type>
        <Type
          variant="chipLabel"
          textAlign="center"
          mb={3}
        >
          Total savings on Dónde:
          {' '}
          <Type
            variant="inherit"
            color="tertiary"
            inline
            fontWeight="regular"
          >
            <Currency 
              amount={maxWithdrawal?.withdraw_max || 0} 
            />
          </Type>
        </Type>
        <Type
          variant="p"
          textAlign="center"
          mb={1}
        >
          Funds you've contributed to Dónde are eligible for withdrawal. Company contributions are subject to their withdrawal policy and multiple requests are subject to review.
        </Type>
        <Type
          variant="dataSmall"
          color="neutralCool_700"
          textAlign="center"
          mb={1.5}
        >
          Dónde Points are not eligible for withdrawal.
        </Type>
        {!user.is_donde_island && !user.is_club_donde && (
          <TextButton
            color="tertiary"
            textAlign="center"
            mb={2.5}
            onClick={() => setActiveIndex(2)}
          >
            What’s my company’s withdrawal policy?
          </TextButton>
        )}
        <Stack
          gap={2}
          mb={3}
        >
          <Input
            icon="$"
            iconColor="mainCool"
            label="Amount"
            mask="currency_no_prefix"
            removeMaskOnChange
            value={amount}
            onChange={setAmount}
          />
          <ExternalAccounts
            label="to"
            showAddAccount
            onSetAccount={setAccount}
          />
          <Select
            label="Reason"
            value={reason}
            onChange={e => {
              setReason(e.target.value)
              if (e.target.value === REASONS[0]) {
                setActiveIndex(3)
              }
            }}
          >
            {REASONS.map(o => (
              <MenuItem
                key={o}
                value={o}
              >
                {o}
              </MenuItem>
            ))}
          </Select>
          {reason === REASONS[4] && (
            <TextArea
              label="Tell Us Why"
              fluid
              noResize
              charCount={300}
              value={customReason}
              onChange={setCustomReason}
            />
          )}
        </Stack>
        <Button
          justifyContent="center"
          disabled={isDisabled}
          onClick={handleRequestWithdrawal}
        >
          Withdraw My Funds
        </Button>
        <Type
          variant="labelSubAtomicMedium"
          color="text.neutral_500"
          textAlign="center"
          mt={1.5}
        >
          By clicking ‘Withdraw My Funds’ you authorize a one-time transfer between your Dónde Wallet and the selected account above. Your authorization is effective immediately. You can cancel your request before it begins processing by contacting us at (213) 463-6633.
        </Type>
      </Stack>
      {/* SUCCESS */}
      <AllSet
        title="Your withdrawal is complete!"
        subtitle="You should see transferred funds in your selected account within 5–10 business days."
        duration={6000}
      />
      {/* POLICY */}
      <StandardModal
        icon="InfoCircle"
        title={`${company?.name}’s policy`}
        subtitle={
          <Box
            color="text.neutralCool_700"
            dangerouslySetInnerHTML={{
              __html: user.is_donde_island || user.is_club_donde ? company?.pto_explainer?.termination_policy : company?.pto_explainer?.withdraw_policy,
            }}
          /> 
        }
        actionOne={
          <span
            onClick={() => setActiveIndex(0)}
          >
            I understand! Go back.
          </span>
        }
        centerAction
      />
      {/* TERMINATION */}
      <StandardModal
        icon="InfoCircle"
        frameIcon
        title={`No longer with by ${company?.name}? No problem!`}
        subtitle={
          <>
            When your employment ends, we move your account & Dónde Wallet to what we call <i>Dónde Island</i>. This means that — even though you don’t receive your company’s contribution — you can still save for travel, access our rates & member deals, work with our Travel Design team, & earn rewards on Dónde.
          </>
        }
        actionOne="Awesome! Cancel withdrawal."
        actionTwo={
          <span
            onClick={() => setActiveIndex(0)}
          >
            Continue my withdrawal
          </span>
        }
        closeModalAction="one"
      />
    </ModalFlow>
  )
}

RequestWithdrawal.propTypes = {
  amount: PropTypes.string,
  reason: PropTypes.string,
}

RequestWithdrawal.defaultProps = {
  drawerTitle: 'Request Withdrawal',
  amount: '',
  reason: '',
}

export default RequestWithdrawal